import { FetchState } from "../../entities/FetchState";
import { IReportDataObject, emptyReportDataObject } from "../../entities/reports/IReportData";
import { Reducer } from "redux";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import Either from "../../utils/Either";
import { ActionCreators as Navigation } from "../navigate"
import {callApiNoResult, callApiWithResult, ICallNoResult, ICallResult, IError} from "../../utils/callApi";

/**********************************************************
 * API Data
 **********************************************************/
export interface IData {
    streamId: string;
    title: string;
    description: string;
    completionPeriod: number;
    forAdmin: boolean;
    mandatory: boolean;
    companyStreamId: string;
    fileStatusId: string;
    statusName: string;
}

export const defaultAutomaticTaskItem: IData = {
    streamId: '',
    title: '',
    description: '',
    completionPeriod: 0,
    forAdmin: false,
    mandatory: false,
    companyStreamId: '',
    fileStatusId: '',
    statusName: ''
};

// Make a default AutomaticTaskDetail with empty values
export const defaultAutomaticTaskDetail = emptyReportDataObject<IData>(defaultAutomaticTaskItem)

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    Item: IReportDataObject<IData>
    UpdateItem: IReportDataObject<IData>
    DeleteItem: IReportDataObject<IData>
}
const InitialState: IState = {
    Item: defaultAutomaticTaskDetail,
    UpdateItem: defaultAutomaticTaskDetail,
    DeleteItem: defaultAutomaticTaskDetail
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    // Fetch action types
    FetchData = "automatictask.AutomaticTaskItem Fetch",
    FetchDataSuccess = "automatictask.AutomaticTaskItem FetchSuccess",
    FetchDataFailed = "automatictask.AutomaticTaskItem FetchFailed",
    
    // Update action types
    UpdateData = "automatictask.AutomaticTaskItem Update",
    UpdateDataSuccess = "automatictask.AutomaticTaskItem UpdateSuccess",
    UpdateDataFailed = "automatictask.AutomaticTaskItem UpdateFailed",
    
    // Delete action types
    DeleteData = "automatictask.AutomaticTaskItem Delete",
    DeleteDataSuccess = "automatictask.AutomaticTaskItem DeleteSuccess",
    DeleteDataFailed = "automatictask.AutomaticTaskItem DeleteFailed"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData,
    payload: { id: string }
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}

// Update Actions
export interface IUpdateData {
    type: ActionTypes.UpdateData,
    payload: { 
        streamId: string,
        title: string,
        description: string,
        completionPeriod: number,
        forAdmin: boolean,
        mandatory: boolean,
        fileStatusId: string
    }
}
export interface IUpdateDataSuccess {
    type: ActionTypes.UpdateDataSuccess,
    payload: { data: IData }
}
export interface IUpdateDataFailed {
    type: ActionTypes.UpdateDataFailed,
    payload: { errorMessage: string }
}

// Delete Actions
export interface IDeleteData {
    type: ActionTypes.DeleteData,
    payload: { streamId: string }
}
export interface IDeleteDataSuccess {
    type: ActionTypes.DeleteDataSuccess,
    payload: { streamId: string }
}
export interface IDeleteDataFailed {
    type: ActionTypes.DeleteDataFailed,
    payload: { errorMessage: string }
}

type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed | 
               IUpdateData | IUpdateDataSuccess | IUpdateDataFailed |
               IDeleteData | IDeleteDataSuccess | IDeleteDataFailed;

export class ActionCreators {
    // Fetch Actions
    public static FetchData(id: string): IFetchData {
        return { 
            type: ActionTypes.FetchData, 
            payload: { id }
        };
    }
    public static FetchDataSuccess(data: IData): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data }
        };
    }
    public static FetchDataFailed(errorMessage: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage }
        };
    }

    // Update Actions
    public static UpdateData(streamId: string, title: string, description: string, completionPeriod: number, forAdmin: boolean, mandatory: boolean, fileStatusId: string): IUpdateData {
        return {
            type: ActionTypes.UpdateData,
            payload: { streamId, title, description, completionPeriod, forAdmin, mandatory, fileStatusId }
        };
    }
    public static UpdateDataSuccess(data: IData): IUpdateDataSuccess {
        return {
            type: ActionTypes.UpdateDataSuccess,
            payload: { data }
        };
    }
    public static UpdateDataFailed(errorMessage: string): IUpdateDataFailed {
        return {
            type: ActionTypes.UpdateDataFailed,
            payload: { errorMessage }
        };
    }

    // Delete Actions
    public static DeleteData(streamId: string): IDeleteData {
        return {
            type: ActionTypes.DeleteData,
            payload: { streamId }
        };
    }
    public static DeleteDataSuccess(streamId: string): IDeleteDataSuccess {
        return {
            type: ActionTypes.DeleteDataSuccess,
            payload: { streamId }
        };
    }
    public static DeleteDataFailed(errorMessage: string): IDeleteDataFailed {
        return {
            type: ActionTypes.DeleteDataFailed,
            payload: { errorMessage }
        };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        // Fetch cases
        case ActionTypes.FetchData:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        
        // Update cases
        case ActionTypes.UpdateData:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.UpdateDataSuccess:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                },
                Item: {
                    ...state.Item,
                    data: action.payload.data
                }
            };
        case ActionTypes.UpdateDataFailed:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };

        // Delete cases
        case ActionTypes.DeleteData:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.DeleteDataSuccess:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Success,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.DeleteDataFailed:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };

        default:
            return state;
    }
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleIFetchData(action: IFetchData) {
    try {
        const response: Either<IError, ICallResult<IData>> = yield call(callApiWithResult, 'GET', `/v1/automatictask/${action.payload.id}`);
        yield response
            .FlatMap(success => put(ActionCreators.FetchDataSuccess(success.result) as any))
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* handleIUpdateData(action: IUpdateData) {
    try {
        const updateDto = {
            title: action.payload.title,
            description: action.payload.description,
            completionPeriod: action.payload.completionPeriod,
            forAdmin: action.payload.forAdmin,
            mandatory: action.payload.mandatory,
            fileStatusId: action.payload.fileStatusId
        };
        
        const response: Either<IError, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/automatictask/${action.payload.streamId}`, updateDto);
        yield response
            .FlatMap(_ => put(ActionCreators.UpdateDataSuccess({...action.payload, companyStreamId: "", statusName: ""}) as any))
            .Reduce(error => put(ActionCreators.UpdateDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.UpdateDataFailed(JSON.stringify(err)))
    }
}

function* handleIDeleteData(action: IDeleteData) {
    try {
        const response: Either<IError, ICallNoResult> = yield call(callApiNoResult, 'DELETE', `/v1/automatictask/${action.payload.streamId}`);
        yield response
            .FlatMap(_ => put(ActionCreators.DeleteDataSuccess(action.payload.streamId) as any))
            .Reduce(error => put(ActionCreators.DeleteDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.DeleteDataFailed(JSON.stringify(err)))
    }
}

function* handleIUpdateDataSuccess(action: IUpdateDataSuccess) {
    yield put(Navigation.ToAutomaticTaskDetail(action.payload.data.streamId))
}

function* handleIDeleteDataSuccess(action: IDeleteDataSuccess) {
    yield put(Navigation.ToAutomaticTaskList())
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleIFetchData)
    yield takeEvery(ActionTypes.UpdateData, handleIUpdateData)
    yield takeEvery(ActionTypes.DeleteData, handleIDeleteData)
    yield takeEvery(ActionTypes.UpdateDataSuccess, handleIUpdateDataSuccess)
    yield takeEvery(ActionTypes.DeleteDataSuccess, handleIDeleteDataSuccess)
}

export function* sagas() {
    yield all([fork(watchAll)]);
}
